import React from "react"
import Breadcrumbs from "components/Common/Breadcrumb"
import {
  Badge,
  Card,
  CardBody,
  Col,
  Container,
  Row,
  Table,
} from "reactstrap"
import Filters from "components/Common/Filters"
import { pageSizes } from "components/Common/constants"
import Switch from "components/Shared/Switch"
import { useState } from "react"
import { useEffect } from "react"
import {
  deleteCountry,
  getAllCountries,
  updateCountryStatus,
} from "store/actions/countriesAction"
import { useDispatch } from "react-redux"
import { useSelector } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Paginations from "components/Common/Paginations"
import { showAlert } from "store/reducers/alertReducer"
import DataExportDropdown from "components/Shared/DataExportDropdown"
import isPermissionEnabled from "common/isPermissionEnabled"
import { isPlatformCountryManager } from "utils/platformCountryAccess"
import {
  getCountriesBasePath,
  getCountriesFallbackDashboard,
} from "utils/countriesRoutes"
import TableShimmer from "components/Shared/TableShimmer"
import TableActionsDropdown from "components/Shared/TableActionsDropdown"
import DeleteModal from "components/Common/DeleteModal"
import { useTranslation } from "react-i18next"
import LtrText from "components/Shared/LtrText"
import ConfirmModal from "components/Common/ConfirmModal"
import { getCurrencyLabel } from "utils/currency"

const Countries = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t("Countries List ")

  const dispatch = useDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const pageNumber = parseInt(searchParams.get("page")) || 1
  const [selectedId, setSelectedId] = useState(null)
  const [isApiCalled, setIsApiCalled] = useState(false)
  const [searchTerm, setSearchTerm] = useState(searchParams.get("search") || "")
  const [selectedFilters, setSelectedFilters] = useState({
    status: parseInt(searchParams.get("status") || "2"),
    sortOrder: parseInt(searchParams.get("sortOrder") || "1"),
  })
  const {
    loading,
    isLoading,
    totalCount,
    totalPages,
    currentPage,
    itemsPerPage,
    countries,
  } = useSelector(state => state.Countries)

  const [itemIdToDelete, setItemIdToDelete] = useState(null)
  const [showDeleteModal, setShowDeleteModal] = useState(false)

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)
  const countriesBasePath = getCountriesBasePath({ accountType, accountScope })
  const isPlatform = isPlatformCountryManager({ accountType, accountScope })
  // VAT is tenant-scoped — hide on super-admin country list.
  const showVatColumn = !isPlatform

  const [usersPermissions, setUsersPermissions] = useState({
    list: null,
    create: null,
    edit: null,
    delete: null,
    manageStatus: null,
  })
  const [showStatusModal, setShowStatusModal] = useState(false)
  const [statusCountryId, setStatusCountryId] = useState(null)
  const [statusToUpdate, setStatusToUpdate] = useState(null)

  // FIRST CHECK PERMISSION, IF DOES'T HAVE THEN REDIRECT TO DASHBOARD
  useEffect(() => {
    const canManageCountries = isPlatformCountryManager({
      accountType,
      accountScope,
    })
    const isPermissionList = isPermissionEnabled(
      userRole,
      11,
      "country_1",
      accountType,
      accountScope
    )
    const canEditTaxShipment = isPermissionEnabled(
      userRole,
      11,
      "country_5",
      accountType,
      accountScope
    )
    // Platform: full country CRUD. Tenant: tax/shipment edit only (no add/delete/status).
    const isPermissionCreate = canManageCountries
    const isPermissionEdit = canManageCountries || canEditTaxShipment
    const isPermissionDelete = canManageCountries
    if (!isPermissionList && !canManageCountries) {
      navigate(getCountriesFallbackDashboard({ accountType, accountScope }))
    }
    setUsersPermissions({
      list: isPermissionList || canManageCountries,
      create: isPermissionCreate,
      edit: isPermissionEdit,
      delete: isPermissionDelete,
      manageStatus: canManageCountries,
    })
  }, [userRole, accountType, accountScope, navigate])

  useEffect(() => {
    getCountries()
  }, [])

  const getCountries = () => {
    const { status, sortOrder } = selectedFilters

    const payload = {
      perPage: itemsPerPage,
      pageNo: pageNumber,
      status: status,
      orderBy: sortOrder,
      keyword: searchTerm,
    }
    dispatch(getAllCountries(payload))
    setIsApiCalled(true)
  }

  const filters = [
    {
      name: "status",
      type: "select",
      options: [
        { label: t("All"), value: 2 },
        { label: t("Enabled"), value: 1 },
        { label: t("Disabled"), value: 0 },
      ],
      placeholder: t("All"),
    },
    {
      name: "sortOrder",
      type: "select",
      options: [
        { label: t("Ascending"), value: 1 },
        { label: t("Descending"), value: 2 },
      ],
      placeholder: t("Ascending"),
    },
  ]

  const handleFetchCountries = (page = 1) => {
    const { status, sortOrder } = selectedFilters

    // Initialize searchParams object
    const searchParams = new URLSearchParams()
    searchParams.set("search", searchTerm)
    searchParams.set("status", status.toString())
    searchParams.set("sortOrder", sortOrder.toString())
    searchParams.set("page", page.toString())

    // Create payload for dispatch
    const payload = {
      perPage: itemsPerPage,
      pageNo: page,
      status: Number(status), // 0 deactive, 1 active, 2 for all
      orderBy: Number(sortOrder),
      keyword: searchTerm || "",
    }

    // Dispatch action and navigate on success
    dispatch(getAllCountries(payload))
      .then(action => {
        const { statusCode, status } = action?.payload
        if (statusCode === 200 && status) {
          navigate(`${countriesBasePath}?${searchParams.toString()}`)
        }
      })
      .catch(error => {
        // Handle error if needed
      })
  }

  const handleSearch = () => {
    handleFetchCountries(1)
  }

  const onPageChange = page => {
    handleFetchCountries(page)
  }

  const handleSearchChange = e => {
    setSearchTerm(e.target.value)
  }
  const handleFilterChange = (selectedValue, filterName) => {
    setSelectedFilters(prevFilters => ({
      ...prevFilters,
      [filterName]: selectedValue,
    }))
  }

  // useEffect(() => {
  //   if (isApiCalled) {
  //     if (searchTerm) {
  //       const delayDebounceFn = setTimeout(() => {
  //         handleSearch()
  //       }, 500)

  //       return () => clearTimeout(delayDebounceFn)
  //     } else {
  //       handleSearch()
  //     }
  //   }
  // }, [searchTerm, selectedFilters])

  const handleReset = () => {
    setSearchTerm("")
    const payload = {
      perPage: itemsPerPage,
      pageNo: 1,
      status: 2,
      orderBy: 1,
      keyword: "",
    }
    dispatch(getAllCountries(payload))
    navigate(`${countriesBasePath}?page=${1}`)
    setSelectedFilters({
      status: 2,
      sortOrder: 1,
    })
  }

  const handleConfirmStatusChange = () => {
    if (!statusCountryId) return
    setSelectedId(statusCountryId)
    const payload = {
      id: statusCountryId,
      status: statusToUpdate.toString(),
    }
    dispatch(updateCountryStatus(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
      .finally(() => {
        setShowStatusModal(false)
        setStatusCountryId(null)
        setStatusToUpdate(null)
      })
  }

  const handleDelete = () => {
    const payload = {
      id: itemIdToDelete,
      status: "2", // 2 FOR DELETE
    }
    dispatch(deleteCountry(payload))
      .then(action => {
        const { statusCode, status, msg } = action?.payload
        if (statusCode === 200 && status) {
          dispatch(showAlert({ text: msg, type: "success" }))
          setShowDeleteModal(false)
          handleFetchCountries(currentPage)
        } else {
          dispatch(showAlert({ text: msg, type: "error" }))
        }
      })
      .catch(error => {})
  }

  const getCountryActionItems = item => [
    ...(usersPermissions.edit
      ? [
          {
            key: "edit",
            content: (
              <>
                <i className="mdi mdi-pencil font-size-16 text-success me-1"></i>
                {t("Edit")}
              </>
            ),
            onClick: () => navigate(`${countriesBasePath}/edit?id=${item._id}`),
          },
        ]
      : []),
    {
      key: "view",
      content: (
        <>
          <i className="mdi mdi-eye font-size-16 text-primary me-1"></i>
          {t("View")}
        </>
      ),
      onClick: () => navigate(`${countriesBasePath}/country-details?id=${item._id}`),
    },
    ...(usersPermissions.delete
      ? [
          {
            key: "delete",
            content: (
              <>
                <i className="mdi mdi-trash-can font-size-16 text-danger me-1"></i>
                {t("Delete")}
              </>
            ),
            onClick: () => {
              setItemIdToDelete(item._id)
              setShowDeleteModal(true)
            },
          },
        ]
      : []),
  ]

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs />
          <Row>
            <Col xl={12}>
              <Card>
                <DataExportDropdown
                  cardTitle={t("Countries List")}
                  isAddBtn={loading ? false : usersPermissions.create}
                  addBtnText={t("Add Country")}
                  onAddBtnClick={() => navigate(`${countriesBasePath}/add`)}
                />
                <CardBody>
                  <Filters
                    filters={filters}
                    searchTerm={searchTerm}
                    handleSearchChange={handleSearchChange}
                    handleFilterChange={handleFilterChange}
                    onReset={handleReset}
                    handleSearch={handleSearch}
                    selectedFilters={selectedFilters}
                    isCustomPageSize={true}
                    pageSizes={pageSizes}
                    itemsPerPage={10}
                    setItemsPerPage={10}
                    searchBy={t("Name, Phone Code, Country Code")}
                  />

                  <div className="">
                    {loading ? (
                      <TableShimmer row={7} col={5} />
                    ) : countries.length === 0 ? (
                      <p className="text-center my-2">
                        {t("No countries found")}
                      </p>
                    ) : (
                      <Table className="table mb-0" hover>
                        <thead className="table-light">
                          <tr>
                            <th>{t("S. No.")}</th>
                            <th>{t("Name")}</th>
                            <th>{t("Phone Code")}</th>
                            <th>{t("Country Code")}</th>
                            <th>{t("Currency")}</th>
                            {showVatColumn && <th>{t("VAT %")}</th>}
                            <th>{t("Status")}</th>
                            <th className="align-middle">{t("Action")}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {countries.map((item, index) => (
                            <tr
                              key={item._id}
                              onClick={() => {
                                navigate(
                                  `${countriesBasePath}/country-details?id=${item._id}`
                                )
                              }}
                              className="cursor-pointer"
                            >
                              <th scope="row">
                                {" "}
                                {(currentPage - 1) * 10 + index + 1}
                              </th>
                              <td>
                                {language === "sa" ? item.nameAr : item.name}
                              </td>
                              <td>
                                {item.dialCode ? (
                                  <LtrText>{item.dialCode}</LtrText>
                                ) : (
                                  "-"
                                )}
                              </td>
                              <td>{item.countryCode}</td>
                              <td>
                                {item.currency
                                  ? getCurrencyLabel(item.currency)
                                  : "-"}
                              </td>
                              {showVatColumn && (
                                <td>{item?.taxInfo?.value}</td>
                              )}
                              <td>
                                {item.status === 1 ? (
                                  <Badge className="bg-success">
                                    {t("Enabled")}
                                  </Badge>
                                ) : (
                                  <Badge className="bg-danger">
                                    {t("Disabled")}
                                  </Badge>
                                )}
                              </td>

                              <td className="align-middle">
                                <div className="d-inline-flex align-items-center gap-2">
                                {usersPermissions.manageStatus && (
                                  <Switch
                                    onClickHandler={e => {
                                      e.stopPropagation()
                                      setStatusCountryId(item._id)
                                      setStatusToUpdate(
                                        item.status === 1 ? 0 : 1
                                      ) // toggle status
                                      setShowStatusModal(true)
                                    }}
                                    isChecked={item.status === 1}
                                    disabled={
                                      item._id === selectedId && isLoading
                                    }
                                  />
                                )}
                                <TableActionsDropdown
                                  items={getCountryActionItems(item)}
                                />
                                </div>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </Table>
                    )}
                  </div>
                  {!loading && countries.length > 0 && (
                    <div className="mt-3">
                      <Paginations
                        data={countries}
                        itemsPerPage={itemsPerPage}
                        onPageChange={onPageChange}
                        currentPage={currentPage}
                        totalPages={totalPages}
                        totalCount={totalCount}
                      />
                    </div>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
      <DeleteModal
        show={showDeleteModal}
        isLoading={isLoading}
        onDeleteClick={handleDelete}
        onCloseClick={() => setShowDeleteModal(false)}
        buttonText={t("Delete")}
        bodyTitle={t("Are you sure you want to delete this {{name}}?", {
          name: t("Country"),
        })}
      />
      <ConfirmModal
        show={showStatusModal}
        isLoading={isLoading}
        onDeleteClick={handleConfirmStatusChange}
        onCloseClick={() => setShowStatusModal(false)}
        buttonText={t("Yes")}
        bodyTitle={t("Are you sure you want to update the status?")}
      />
    </React.Fragment>
  )
}

export default Countries
